import { useUser } from "@/providers/user";
import { OptionsResponse } from "@/types/dynamic-detail";
import React from "react";
import { useLoaderData, useParams } from "react-router-dom";
import { RelatedList } from "../layout-2/related-list";
import { Breadcrumb, BreadcrumbItem, BreadcrumbLink, BreadcrumbList, BreadcrumbPage, BreadcrumbSeparator } from "../ui/breadcrumb";

interface GenericDetailRelatedObjectProps {
    objectType: string;
    objectId: string;
    breadcrumbs: { name: string; href?: string }[];
}

export function GenericDetailRelatedObject({ objectType, objectId, breadcrumbs }: GenericDetailRelatedObjectProps) {
    const params = useParams();
    const relationshipName = params.relationshipName!;

    const { user, has_any_group } = useUser();
    const { metadata, object } = useLoaderData() as {
        metadata: OptionsResponse;
        object?: { owner?: { id?: number | null } | null };
    };
    const ownerId = object?.owner?.id;
    const canViewMatchAs =
        Boolean(user?.is_super_admin || user?.is_manager) || has_any_group(["app_apprenticeship_admin", "app_crm_admin"]);

    const relatedObject = metadata.detail_config.related_objects.find(relatedObject => relatedObject.relationship_name === relationshipName);

    if (!relatedObject) {
        return (
            <div className="flex items-center justify-center p-8">
                <div className="text-red-500">No related object found for {relationshipName}</div>
            </div>
        );
    }

    const headerLinks =
        canViewMatchAs && ownerId
            ? relatedObject.header_links?.map(link =>
                  link.href_template.startsWith("/alternance/matchs") && !link.href_template.includes("user_id=")
                      ? { ...link, href_template: link.href_template.replace("/alternance/matchs", `/alternance/matchs?user_id=${ownerId}`) }
                      : link
              )
            : relatedObject.header_links;

    const onRowNavigateTo = relatedObject.href_template
        ? (instance: any, _location?: any) => {
              const detailUrl = instance.href || relatedObject.href_template!.replace("{id}", instance.id);
              return detailUrl.includes("{") ? null : detailUrl;
          }
        : undefined;
    return (
        <div className="flex flex-col gap-5 pb-11">
            <div className="flex w-full flex-col items-start justify-between gap-4 sm:flex-row sm:items-center lg:col-span-2">
                {breadcrumbs && (
                    <Breadcrumb>
                        <BreadcrumbList>
                            {breadcrumbs.map((item, index) =>
                                index < breadcrumbs.length - 1 ? (
                                    <React.Fragment key={`${item.name}_${index}`}>
                                        <BreadcrumbItem>
                                            <BreadcrumbLink href={item.href ?? ""}>{item.name.slice(0, 50)}</BreadcrumbLink>
                                        </BreadcrumbItem>
                                        <BreadcrumbSeparator />
                                    </React.Fragment>
                                ) : (
                                    <BreadcrumbPage key={`${index}-${item.name}`}>{item.name.slice(0, 50) && item.name.length > 50 ? item.name.slice(0, 50) + "..." : item.name}</BreadcrumbPage>
                                )
                            )}
                        </BreadcrumbList>
                    </Breadcrumb>
                )}
            </div>
            <RelatedList
                objectApiUrl={`/crm/objects/${metadata.object_url ?? objectType}`}
                objectId={objectId}
                relatedName={relatedObject.label}
                relatedCode={relatedObject.relationship_name}
                fields_to_display={relatedObject.fields_to_display}
                clickable_field={Boolean(relatedObject.href_template) ? relatedObject.clickable_field || relatedObject.fields_to_display[0]?.field_name : undefined}
                onRowNavigateTo={onRowNavigateTo}
                icon={relatedObject.object_metadata?.icon}
                color={relatedObject.object_metadata?.color}
                hide_if_empty={relatedObject.hide_if_empty}
                pageSize={250}
                form={relatedObject.form}
                actions={relatedObject.actions}
                rowActions={relatedObject.row_actions}
                parentRowActions={relatedObject.parent_row_actions}
                headerLinks={headerLinks}
                childApiUrl={relatedObject.object_metadata?.url ? `/crm/objects/${relatedObject.object_metadata.url}` : undefined}
            />
        </div>
    );
}
